import {useCallback, useMemo, useRef, useSyncExternalStore} from "react";

export type ViewportRect = {top: number; left: number; width: number; height: number}

/**
 * Fixed overlay box aligned to a table panel: subscribes to scroll/resize so `getBoundingClientRect`
 * stays in sync (used by portaled table empty states).
 */
export function useTablePanelViewportRect(boundsElement: HTMLElement | null): ViewportRect | null {
    const generationRef = useRef(0);

    const subscribe = useCallback(
        (onStoreChange: () => void) => {
            if (typeof window === "undefined" || boundsElement === null) {
                return () => {};
            }
            const bump = () => {
                generationRef.current += 1;
                onStoreChange();
            };
            const ro = new ResizeObserver(bump);
            ro.observe(boundsElement);
            window.addEventListener("scroll", bump, true);
            window.addEventListener("resize", bump);
            bump();
            return () => {
                ro.disconnect();
                window.removeEventListener("scroll", bump, true);
                window.removeEventListener("resize", bump);
            };
        },
        [boundsElement],
    );

    const generation = useSyncExternalStore(
        subscribe,
        () => generationRef.current,
        () => 0,
    );

    return useMemo(() => {
        if (typeof window === "undefined" || boundsElement === null) {
            return null;
        }
        const r = boundsElement.getBoundingClientRect();
        return {top: r.top, left: r.left, width: r.width, height: r.height};
    }, [boundsElement, generation]);
}
